import {
  ComponentProps,
  ComponentType,
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
} from "react";
import { parseUrlDate, parseUrlNum, useUrlParams } from "~@/hooks/useUrlParams";

export type ViewMode = "grid" | "list";

export type DateViewType = "daily" | "weekly";

export type ScheduleContext = {
  categoryId: number | undefined;
  setCategoryId: Dispatch<SetStateAction<number | undefined>>;
  teamId: number | undefined;
  setTeamId: Dispatch<SetStateAction<number | undefined>>;
  venueId: number | undefined;
  setVenueId: Dispatch<SetStateAction<number | undefined>>;
  startDate: Date | null;
  setStartDate: Dispatch<SetStateAction<Date | null>>;
  endDate: Date | null;
  setEndDate: Dispatch<SetStateAction<Date | null>>;
  viewMode: ViewMode;
  setViewMode: Dispatch<SetStateAction<ViewMode>>;
  dateViewType: DateViewType;
  setDateViewType: Dispatch<SetStateAction<DateViewType>>;
};

// eslint-disable-next-line @typescript-eslint/no-redeclare -- companion object
export const ScheduleContext = createContext<ScheduleContext | undefined>(
  undefined
);

export const useScheduleContext = () => {
  const context = useContext(ScheduleContext);
  if (!context) throw new Error("Missing ScheduleProvider.");
  return context;
};

export const ScheduleProvider: FC<PropsWithChildren> = ({ children }) => {
  const { searchParams, makeNumSetter, makeDateSetter, makeStrSetter } = useUrlParams();

  const categoryId = parseUrlNum(searchParams, "sc_cat");
  const teamId = parseUrlNum(searchParams, "sc_team");
  const venueId = parseUrlNum(searchParams, "sc_venue");

  const startDate = useMemo(() => parseUrlDate(searchParams, "sc_date"), [searchParams]);
  const endDate = useMemo(() => parseUrlDate(searchParams, "sc_end"), [searchParams]);
  const viewMode = (searchParams.get("sc_view") as ViewMode) || "grid";
  const dateViewType = (searchParams.get("sc_dview") as DateViewType) || "daily";

  const setCategoryId = useMemo(() => makeNumSetter("sc_cat"), [makeNumSetter]);
  const setTeamId = useMemo(() => makeNumSetter("sc_team"), [makeNumSetter]);
  const setVenueId = useMemo(() => makeNumSetter("sc_venue"), [makeNumSetter]);
  const setStartDate = useMemo(() => makeDateSetter("sc_date"), [makeDateSetter]);
  const setEndDate = useMemo(() => makeDateSetter("sc_end"), [makeDateSetter]);
  const setViewMode = useMemo(() => makeStrSetter("sc_view", "grid" as ViewMode), [makeStrSetter]);
  const setDateViewType = useMemo(() => makeStrSetter("sc_dview", "daily" as DateViewType), [makeStrSetter]);

  return (
    <ScheduleContext.Provider
      value={{
        categoryId,
        setCategoryId,
        teamId,
        setTeamId,
        venueId,
        setVenueId,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        viewMode,
        setViewMode,
        dateViewType,
        setDateViewType,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};

export const withScheduleProvider = <P extends object>(
  Component: ComponentType<P>
): FC<P> => {
  return function WithScheduleProvider(
    props: ComponentProps<typeof Component>
  ) {
    return (
      <ScheduleProvider>
        <Component {...props} />
      </ScheduleProvider>
    );
  };
};
